// Auto Cover Page Helper
// -----------------------
// Purpose:
// Keep the Cover Page in sync with the clinician-approved core sections.
// We re-render the Cover Page automatically on exit of ANY review page when:
// - report_logic_version = 2
// - all required sections for the patient's flow are approved
// - Cover Page is stale compared to the latest section review times
//
// ADHD-positive path: requires CI(1), SSI(2), DD(3), PAD(4)
// Not-ADHD + SSI-always path: requires CI(1), SSI(2), DD(3), PAD(4)
// Not-ADHD + standard path: requires CI(1), DD(3) only
//
// Wait-then-retry for concurrency:
// When the cover page is already processing (another caller won the race), we wait
// for it to finish and then re-check staleness.  If still stale, we trigger exactly
// one retry.  This guarantees correctness when multiple core sections are re-approved
// in rapid succession.

import { db } from '../firebase';
import { doc, getDoc } from 'firebase/firestore';
import { checkReportLogicVersion } from './featureFlagUtils';
import { isAdhdAssessmentFlow } from './conditionChecks';
import { resolveWorkflowConfig } from '../hooks/useWorkflowConfig';
import { TriggerCoverPage } from './TriggerCoverPage';
import { waitForProcessingComplete } from './processingStatusHelpers';
import { getRequiredCoreSections, areAllRequiredSectionsApproved } from './requiredSectionsUtils';

// Utility: Parse Firestore Timestamp | Date | ISO string into a JS Date, or null
const toDateOrNull = (value) => {
  if (!value) return null;
  try {
    if (value.toDate && typeof value.toDate === 'function') return value.toDate();
    if (value instanceof Date) return value;
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  } catch {
    return null;
  }
};

const getManageReports = (data) => data?.dataModel?.['6_manageReports'] || {};

// Required sections logic is centralised in requiredSectionsUtils.js.
// See that module for business rules (ADHD flow, SSI-always orgs, etc.).

const getSectionsLastReviewedMaxAt = (manageReports, requiredSections) => {
  const sections = manageReports?.sections || {};
  const lastReviewedDates = requiredSections
    .map((n) => toDateOrNull(sections?.[`${n}`]?.lastReviewedAt))
    .filter(Boolean);
  if (lastReviewedDates.length === 0) return null;
  return new Date(Math.max(...lastReviewedDates.map((d) => d.getTime())));
};

const isCoverProcessing = (manageReports) => manageReports?.cpReportProcessingStatus === 'processing';

const isCoverStale = (manageReports, requiredSections) => {
  const approvedAt = toDateOrNull(manageReports?.cpReportApprovedAt);
  if (!approvedAt) return true; // first-time render
  const sectionsMaxReviewedAt = getSectionsLastReviewedMaxAt(manageReports, requiredSections);
  if (!sectionsMaxReviewedAt) return true; // allow bootstrap
  return approvedAt.getTime() < sectionsMaxReviewedAt.getTime();
};

// Small retry helper for eventual consistency on serverTimestamp propagation
const retry = async (fn, attempts = 3, delaysMs = [150, 400, 800]) => {
  let lastError;
  for (let i = 0; i < attempts; i++) {
    try {
      return await fn();
    } catch (e) {
      lastError = e;
      if (i < attempts - 1) {
        await new Promise((res) => setTimeout(res, delaysMs[i] || 500));
      }
    }
  }
  throw lastError;
};

// Internal: derive the cover page report type string from patient data.
const determineCoverPageType = (patientDataModel, organisation) => {
  const wfForCover = resolveWorkflowConfig(patientDataModel, { organisation });
  const adhdFlow = isAdhdAssessmentFlow(patientDataModel, wfForCover);
  return adhdFlow ? 'Cover Page - Regular' : 'Cover Page - Not ADHD - CI';
};

// Internal: re-read the latest patient document and check if the cover page
// is stale.  Always uses the live document snapshot for flow-sensitive
// decisions (ADHD path, required sections) to avoid stale closure data from
// the React context.
// Returns { freshData, manageReports, requiredSections, stale, ... } or throws.
const checkCoverStaleness = async (patientId, organisation) => {
  const patientRef = doc(db, 'patients', patientId);
  const snap = await getDoc(patientRef);
  if (!snap.exists()) throw new Error('patient-doc-missing');
  const freshData = snap.data();
  const manageReports = getManageReports(freshData);
  const requiredSections = getRequiredCoreSections(freshData, organisation);
  return {
    freshData,
    manageReports,
    requiredSections,
    stale: isCoverStale(manageReports, requiredSections),
    processing: isCoverProcessing(manageReports),
    allApproved: areAllRequiredSectionsApproved(manageReports, requiredSections),
  };
};

// Public API: rerenderCoverPageIfStale
// Safe to call after exiting CI/SSI/DD/PAD. Fire-and-forget from the UI.
//
// When the cover page is already processing, instead of bailing out we wait
// for the in-flight generation to complete, then re-check staleness.  If still
// stale (because the CF started before the latest section data was written),
// we trigger exactly one retry.
export const rerenderCoverPageIfStale = async ({ patientId, clinicianId, patientDataModel, organisation }) => {
  if (!patientId || !clinicianId) {
    return { success: false, skipped: true, reason: 'missing-params' };
  }

  try {
    // Cache feature flag per patientId
    if (!window.__reportLogicVersionCache) window.__reportLogicVersionCache = new Map();
    let version = window.__reportLogicVersionCache.get(patientId);
    if (version === undefined) {
      version = await checkReportLogicVersion(patientId);
      window.__reportLogicVersionCache.set(patientId, version);
    }
    if (version !== 2) return { success: true, skipped: true, reason: 'not-report-logic-version-2' };

    // Fresh read (with retry) to avoid races with serverTimestamp writes
    const readLatest = async () => {
      const patientRef = doc(db, 'patients', patientId);
      const snap = await getDoc(patientRef);
      if (!snap.exists()) throw new Error('patient-doc-missing');
      return snap.data();
    };

    // Use the fresh Firestore data for all flow-sensitive decisions (ADHD path,
    // required sections, cover page type) — the patientDataModel from the React
    // context closure may be stale and not reflect writes from handleExitReview.
    const data = await retry(readLatest, 3);

    // TOC and SLaM Report Builder workflows do not use a standalone Cover Page
    // — skip entirely so we do not write cpReport* with no UI row to surface it.
    const wfForGate = resolveWorkflowConfig(data, { organisation });
    if (wfForGate.replaceCARwithReportBuilder) {
      return { success: true, skipped: true, reason: 'no-standalone-cover-page' };
    }

    const manageReports = getManageReports(data);
    const requiredSections = getRequiredCoreSections(data, organisation);

    // Gate: required sections fully approved
    if (!areAllRequiredSectionsApproved(manageReports, requiredSections)) {
      // If stuck processing while not eligible, reset to idle to avoid infinite spinner
      if (isCoverProcessing(manageReports)) {
        try {
          const patientRef = doc(db, 'patients', patientId);
          await import('firebase/firestore').then(({ updateDoc }) =>
            updateDoc(patientRef, { 'dataModel.6_manageReports.cpReportProcessingStatus': 'idle' })
          );
        } catch {}
      }
      return { success: true, skipped: true, reason: 'not-all-approved' };
    }

    // Gate: skip if not stale
    if (!isCoverStale(manageReports, requiredSections)) {
      return { success: true, skipped: true, reason: 'not-stale' };
    }

    // If another caller is already generating the cover page, wait for it to
    // finish, then re-check staleness and retry once if needed.
    if (isCoverProcessing(manageReports)) {
      console.log('Cover page already processing — waiting for completion before re-checking staleness');
      await waitForProcessingComplete(patientId, 'cpReportProcessingStatus');

      const recheck = await checkCoverStaleness(patientId, organisation);
      if (!recheck.stale || recheck.processing || !recheck.allApproved) {
        return { success: true, skipped: true, reason: 'resolved-after-wait' };
      }

      console.log('Cover page still stale after in-flight generation — triggering retry');
      const retryReportType = determineCoverPageType(recheck.freshData, organisation);
      return await TriggerCoverPage({ clinicianId, patientId, reportType: retryReportType });
    }

    // Happy path: not processing and stale — trigger generation.
    // TriggerCoverPage is fire-and-forget (returns immediately after the
    // transactional guard), so we wait for the processing to complete before
    // re-checking staleness for a possible retry.
    const reportType = determineCoverPageType(data, organisation);
    const result = await TriggerCoverPage({ clinicianId, patientId, reportType });

    try {
      await waitForProcessingComplete(patientId, 'cpReportProcessingStatus');
      const recheck = await checkCoverStaleness(patientId, organisation);
      if (recheck.stale && !recheck.processing && recheck.allApproved) {
        console.log('Cover page stale after generation — triggering one retry');
        const retryReportType = determineCoverPageType(recheck.freshData, organisation);
        await TriggerCoverPage({ clinicianId, patientId, reportType: retryReportType });
      }
    } catch (retryErr) {
      console.error('Post-generation cover page staleness recheck failed:', retryErr);
    }

    return result;
  } catch (error) {
    console.error('rerenderCoverPageIfStale error:', error);
    return { success: false, error: error.message || 'unknown' };
  }
};

export const __testOnly = {
  toDateOrNull,
  getSectionsLastReviewedMaxAt,
  isCoverStale,
  isCoverProcessing,
  checkCoverStaleness,
  determineCoverPageType,
};


